import { Injectable } from '@angular/core';

import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents } from '../../shared/instrumentation/instrumentation.service';
import { OfficeRouteService } from '../../shared/services/office-route.service';
import { PageVisibilityService } from '../../shared/services/page-visibility.service';
import { Performance } from '../../shared/services/performance.service';
import { UpdateContentService } from '../../shared/services/update-content.service';
import { PwaConfigService } from './pwa-config.service';

@Injectable({
    providedIn: 'root'
})
export class PwaDiagnosticsService {

    private static readonly bytesPerMB = 1048576;
    private diagnosticTimer: ReturnType<typeof setTimeout>;
    private logIndex = 0;
    private pageIndex = 0;
    private lastCpuMeasureTime: number;
    private lastProcessCpu: number;
    private lastUserCpu: number;
    private lastIdleCpu: number;

    constructor(
        private readonly instrumentationService: InstrumentationService,
        officeRouteService: OfficeRouteService,
        private readonly pageVisibilityService: PageVisibilityService,
        private readonly pwaConfigService: PwaConfigService,
        private readonly updateContentService: UpdateContentService
    ) {
        officeRouteService.subscribeToRouteChangeEnd(() => { this.pageIndex++; });
        // Warmup initial cpu measures
        this.getDiagnostics(-1);
    }

    public startTrackingDiagnostics() {
        // Log initial diagnostics, then schedule a measurement in 10s, then every app focus
        this.queueLog();
        setTimeout(() => {
            if (this.logIndex <= 1) {
                this.queueLog();
            }
        }, 10000);
        this.updateContentService.getOrAddUpdateStream('diagnostics', () => {
            this.queueLog();
            return Promise.resolve();
        });
        this.pageVisibilityService.addOnVisibilityChanged(state => {
            if (state === 'hidden' && this.diagnosticTimer) {
                clearTimeout(this.diagnosticTimer);
                this.diagnosticTimer = null;
            }
        });
    }

    private queueLog() {
        if (!this.diagnosticTimer) {
            this.diagnosticTimer = setTimeout(() => this.logDiagnostics(), this.pwaConfigService.config.diagnosticsTimeoutInSeconds * 1000);
        }
    }

    private logDiagnostics() {
        this.diagnosticTimer = null;
        if (!this.pageVisibilityService.isVisible) {
            return;
        }
        const diagnostics = this.getDiagnostics(this.logIndex++);
        this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.MemoryUsage, diagnostics, [], DataSecurityLevel.Enhanced);
    }

    private getDiagnostics(index: number): InstrumentationParams {
        const diagnosticsStart = Performance.now();
        const params: InstrumentationParams = {};
        params[InstrumentationParam.Index] = index.toString();
        params[InstrumentationParam.ItemCount] = this.pageIndex.toString();

        if (this.pwaConfigService.config.mock) {
            return params;
        }

        // Log usage in MB
        const appReport = Windows.System.MemoryManager.getAppMemoryReport();
        params[InstrumentationParam.PeakPrivateCommit] = this.getValueAsStringInMb(appReport.peakPrivateCommitUsage);
        params[InstrumentationParam.PrivateCommit] = this.getValueAsStringInMb(appReport.privateCommitUsage);
        params[InstrumentationParam.TotalCommit] = this.getValueAsStringInMb(appReport.totalCommitUsage);
        const processReport = Windows.System.MemoryManager.getProcessMemoryReport();
        params[InstrumentationParam.PrivateWorkingSet] = this.getValueAsStringInMb(processReport.privateWorkingSetUsage);
        params[InstrumentationParam.TotalWorkingSet] = this.getValueAsStringInMb(processReport.totalWorkingSetUsage);

        // In rare cases diagnostic settings can be disabled
        try {
            const processDiagnostics = Windows.System.Diagnostics.ProcessDiagnosticInfo.getForCurrentProcess();
            const memoryUsage = processDiagnostics.memoryUsage.getReport();
            const diskUsage = processDiagnostics.diskUsage.getReport();
            params[InstrumentationParam.PeakTotalWorkingSet] = this.getValueAsStringInMb(memoryUsage.peakWorkingSetSizeInBytes);
            params[InstrumentationParam.DiskWrite] = this.getValueAsStringInMb(diskUsage.bytesWrittenCount);

            // System diagnostics may be more likely to be disabled so we check them later
            const systemDiagnostics = Windows.System.Diagnostics.SystemDiagnosticInfo.getForCurrentSystem();
            const systemMemoryUsage = systemDiagnostics.memoryUsage.getReport();
            const totalSystemMemory = systemMemoryUsage.totalPhysicalSizeInBytes;
            const availableSystemMemory = systemMemoryUsage.availableSizeInBytes;
            params[InstrumentationParam.SystemMemoryTotal] = this.getValueAsStringInMb(totalSystemMemory);
            params[InstrumentationParam.SystemMemoryUsage] = Math.ceil(100 * (1 - (availableSystemMemory / totalSystemMemory))).toString();
            const processCpuUsage = processDiagnostics.cpuUsage.getReport();
            const systemCpuUsage = systemDiagnostics.cpuUsage.getReport();
            const currentCpuMeasureTime = Performance.now();
            const currentUserCpu = systemCpuUsage.userTime;
            const currentIdleCpu = systemCpuUsage.idleTime;
            const currentProcessCpu = processCpuUsage.userTime + processCpuUsage.kernelTime;
            if (this.lastCpuMeasureTime && this.lastProcessCpu && this.lastUserCpu && this.lastIdleCpu) {
                const timeSinceLastMeasure = currentCpuMeasureTime - this.lastCpuMeasureTime;
                const userCpuSinceLastMeasure = currentUserCpu - this.lastUserCpu;
                const idleCpuSinceLastMeasure = currentIdleCpu - this.lastIdleCpu;
                const processCpuSinceLastMeasure = currentProcessCpu - this.lastProcessCpu;
                // Note that we are ignoring systemCpuUsage.kernelTime because in testing it seemed to be erroneously high
                const totalCpuSinceLastMeasure = userCpuSinceLastMeasure + idleCpuSinceLastMeasure;
                params[InstrumentationParam.SystemCpu] = Math.ceil(100 * userCpuSinceLastMeasure / totalCpuSinceLastMeasure).toString();
                params[InstrumentationParam.ProcessCpu] = Math.ceil(100 * processCpuSinceLastMeasure / totalCpuSinceLastMeasure).toString();
                params[InstrumentationParam.ProcessorCount] = Math.ceil(totalCpuSinceLastMeasure / timeSinceLastMeasure).toString();
            }

            this.lastProcessCpu = currentProcessCpu;
            this.lastUserCpu = currentUserCpu;
            this.lastIdleCpu = currentIdleCpu;
            this.lastCpuMeasureTime = currentCpuMeasureTime;
        } catch {
            // Do nothing
        }

        params[InstrumentationParam.Latency] = Math.ceil(Performance.now() - diagnosticsStart).toString();
        return params;
    }

    private getValueAsStringInMb(valueInBytes: number): string {
        return Math.ceil(valueInBytes / PwaDiagnosticsService.bytesPerMB).toString();
    }
}
